// Bleed — the cash-flow recovery queue.
//
// Surfaces squish-backend's margin-ledger bleed report (proxied via
// squish-crm-backend → GET /api/billing/bleed-report). The daily email is the
// push; this is the pull. Two jobs:
//   1. RECOVERY QUEUE — supplier-backed lines whose recovery window is closing
//      or already overdue. Each one is money we can still get back IF someone
//      cancels at the supplier (NameSilo has no API) and then clicks Recovered.
//   2. UNDERWATER — lines where we've paid out more than we kept. Each row
//      carries the same Recovered / Write off pair as the queue: a line whose
//      supplier cost DID come back (a Pax8 cancel inside the NCE window that
//      only the customer door used to record) is cleared here, and a real
//      loss is written off here, so the daily email stops repeating it.
//
// Marking Recovered / Write off hits the same endpoints the loop uses, so the
// email and this page stay in sync.
//
// NAMESILO CANCELLATIONS (top of the page). Every domain refund, self-serve or
// staff, leaves a cancel to make by hand at namesilo.com before NameSilo's
// window closes (squish-backend lib/namesilo-cancellations.js). The daily Slack
// digest says how many and how soon; this list says which, and the checkbox
// records that someone did it. "Not cancelling" closes a row for a goodwill
// refund on a domain the customer keeps.

import React, { useEffect, useState, useCallback } from 'react';
import { getHeaders } from '../api';

const API = import.meta.env.PROD ? '' : 'https://crm-api.squish.co';

const usd = (cents) => {
  if (cents == null || !Number.isFinite(cents)) return '—';
  return `$${(cents / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

// msUntilClose: positive = time left, negative = overdue.
function formatWindow(ms) {
  if (ms == null) return { label: '—', overdue: false };
  const overdue = ms < 0;
  const abs = Math.abs(ms);
  const h = Math.floor(abs / 3_600_000);
  const m = Math.floor((abs % 3_600_000) / 60_000);
  const span = h >= 24 ? `${Math.floor(h / 24)}d ${h % 24}h` : h >= 1 ? `${h}h ${m}m` : `${m}m`;
  return { label: overdue ? `${span} overdue` : `closes in ${span}`, overdue };
}

function StatCard({ label, value, tone }) {
  const color = tone === 'red' ? 'var(--accent2)' : tone === 'green' ? 'var(--accent3)' : 'var(--text)';
  return (
    <div style={{ flex: '1 1 140px', background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 12, padding: '18px 20px' }}>
      <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--text2)', marginBottom: 8 }}>{label}</div>
      <div style={{ fontSize: 26, fontWeight: 700, color }}>{value}</div>
    </div>
  );
}

// The Recovered / Write off pair. One component so the queue and the
// underwater list offer the same two answers and send the same two calls.
function LineActions({ lineId, busyLine, onAct }) {
  const busy = busyLine === lineId;
  return (
    <div style={{ display: 'flex', gap: 8 }}>
      <button data-write disabled={busy} onClick={() => onAct(lineId, 'recovered')}
        style={{ background: 'rgba(109,250,188,0.12)', color: 'var(--accent3)', border: '1px solid rgba(109,250,188,0.3)', borderRadius: 7, padding: '6px 12px', fontSize: 12, fontWeight: 600, cursor: busyLine ? 'default' : 'pointer', opacity: busy ? 0.5 : 1 }}>
        ✓ Recovered
      </button>
      <button data-write disabled={busy} onClick={() => onAct(lineId, 'written-off')}
        style={{ background: 'var(--bg3)', color: 'var(--text2)', border: '1px solid var(--border)', borderRadius: 7, padding: '6px 12px', fontSize: 12, fontWeight: 600, cursor: busyLine ? 'default' : 'pointer', opacity: busy ? 0.5 : 1 }}>
        ✗ Write off
      </button>
    </div>
  );
}

// Underwater lines, each with its disposition. Exported so the test renders
// it with a report-shaped payload. Every line the report lists here is
// non-terminal by construction (written-off lines are excluded server-side
// and a recovered line nets to zero), so both buttons are always valid.
export function UnderwaterLines({ uw, busyLine = null, onAct }) {
  const worst = uw?.worst || [];
  if (worst.length === 0) {
    return (
      <div style={{ padding: '20px', background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 12, color: 'var(--text2)', fontSize: 14 }}>
        ✓ Nothing underwater.
      </div>
    );
  }
  return (
    <div style={{ background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 12, overflow: 'hidden' }}>
      {worst.map((l, i) => (
        <div key={l.lineId} style={{ display: 'flex', alignItems: 'center', gap: 16, padding: '12px 18px', borderTop: i ? '1px solid var(--border)' : 'none', flexWrap: 'wrap' }}>
          <div style={{ flex: '1 1 200px', minWidth: 0 }}>
            <div style={{ color: 'var(--text)', fontSize: 14, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {l.productId || l.sku || l.lineId} <span style={{ color: 'var(--text2)' }}>· {l.supplier || '—'}</span>
            </div>
            <div style={{ color: 'var(--text2)', fontSize: 12, marginTop: 2 }}>{l.customerEmail || '—'}</div>
          </div>
          <div style={{ color: 'var(--accent2)', fontSize: 14, fontWeight: 600, minWidth: 64, textAlign: 'right' }}>{usd(l.netCents)}</div>
          <LineActions lineId={l.lineId} busyLine={busyLine} onAct={onAct} />
        </div>
      ))}
    </div>
  );
}

export default function Bleed() {
  const [report, setReport]   = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);
  const [busyLine, setBusyLine] = useState(null);
  const [cancellations, setCancellations] = useState(null);
  const [busyRow, setBusyRow] = useState(null);

  const loadCancellations = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/billing/namesilo-cancellations?status=open`, { headers: getHeaders() });
      if (!res.ok) throw new Error(`Request failed (${res.status})`);
      const body = await res.json();
      // Re-based on OUR clock: the server's nowMs only says when it answered.
      setCancellations({ rows: body.rows || [], loadedAtMs: Date.now() });
    } catch (err) {
      setCancellations({ rows: [], error: err.message, loadedAtMs: Date.now() });
    }
  }, []);

  useEffect(() => { loadCancellations(); }, [loadCancellations]);


  const load = useCallback(async () => {
    try {
      setError(null);
      const res = await fetch(`${API}/api/billing/bleed-report`, { headers: getHeaders() });
      if (!res.ok) {
        let detail = '';
        try { const b = await res.json(); detail = b?.error || JSON.stringify(b); } catch { /* ignore */ }
        throw new Error(detail || `Request failed (${res.status})`);
      }
      setReport(await res.json());
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const resolveRow = useCallback(async (id, resolution) => {
    setBusyRow(id);
    try {
      const res = await fetch(`${API}/api/billing/namesilo-cancellations/${encodeURIComponent(id)}/resolve`, {
        method: 'POST', headers: getHeaders(), body: JSON.stringify({ resolution }),
      });
      if (!res.ok) {
        let detail = '';
        try { const b = await res.json(); detail = b?.error || JSON.stringify(b); } catch { /* ignore */ }
        throw new Error(detail || `Action failed (${res.status})`);
      }
      // Ticking it also marks the margin-ledger line recovered, so the
      // recovery list below changes too.
      await Promise.all([loadCancellations(), load()]);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusyRow(null);
    }
  }, [loadCancellations, load]);

  const act = useCallback(async (lineId, kind) => {
    const path = kind === 'recovered' ? 'recovered' : 'written-off';
    const note = kind === 'recovered' ? 'Confirmed cancelled at supplier' : 'Loss accepted';
    setBusyLine(lineId);
    try {
      const res = await fetch(`${API}/api/billing/ledger/${encodeURIComponent(lineId)}/${path}`, {
        method: 'POST', headers: getHeaders(), body: JSON.stringify({ note }),
      });
      if (!res.ok) {
        let detail = '';
        try { const b = await res.json(); detail = b?.error || JSON.stringify(b); } catch { /* ignore */ }
        throw new Error(detail || `Action failed (${res.status})`);
      }
      await load();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusyLine(null);
    }
  }, [load]);

  if (loading) return <div style={{ padding: 32, color: 'var(--text2)' }}>Loading bleed report…</div>;
  if (error && !report) return <div style={{ padding: 32, color: 'var(--accent2)' }}>Couldn’t load bleed report: {error}</div>;

  const uw = report?.underwater || { count: 0, bleedCents: 0, bySupplier: {}, worst: [] };
  const rec = report?.recovery || { closingSoon: [], closingSoonCount: 0, missedCount: 0, missedCents: 0 };

  return (
    <div style={{ padding: 32, maxWidth: 980 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 24 }}>
        <h1 style={{ fontSize: 24, fontWeight: 700, color: 'var(--text)', margin: 0 }}>Bleed</h1>
        <button onClick={() => { load(); loadCancellations(); }} style={{ background: 'var(--bg3)', color: 'var(--text2)', border: '1px solid var(--border)', borderRadius: 8, padding: '6px 12px', fontSize: 12, cursor: 'pointer' }}>↻ Refresh</button>
      </div>

      {error && <div style={{ marginBottom: 16, color: 'var(--accent2)', fontSize: 13 }}>{error}</div>}

      <div style={{ display: 'flex', gap: 14, marginBottom: 28, flexWrap: 'wrap' }}>
        <StatCard label="Underwater" value={usd(uw.bleedCents)} tone={uw.bleedCents > 0 ? 'red' : 'green'} />
        <StatCard label="Windows closing" value={rec.closingSoonCount} tone={rec.closingSoonCount > 0 ? 'red' : 'green'} />
        <StatCard label="Sunk (unbooked)" value={`${rec.missedCount} · ${usd(rec.missedCents)}`} tone={rec.missedCount > 0 ? 'red' : undefined} />
      </div>

      {/* NameSilo cancellations — cancel at namesilo.com, then tick */}
      <h2 style={{ fontSize: 13, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--text2)', marginBottom: 6 }}>
        NameSilo cancellations
      </h2>
      <div style={{ color: 'var(--text2)', fontSize: 12, marginBottom: 12 }}>
        namesilo.com → Order History → open the order → red X in the Cancel column. Then tick it here.
      </div>
      {!cancellations ? (
        <div style={{ padding: 20, color: 'var(--text2)', fontSize: 13, marginBottom: 28 }}>Loading…</div>
      ) : cancellations.error ? (
        <div style={{ padding: 20, color: 'var(--accent2)', fontSize: 13, marginBottom: 28 }}>Couldn’t load the cancellation queue: {cancellations.error}</div>
      ) : cancellations.rows.length === 0 ? (
        <div style={{ padding: '20px', background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 12, color: 'var(--text2)', fontSize: 14, marginBottom: 28 }}>
          ✓ Nothing to cancel at NameSilo.
        </div>
      ) : (
        <div style={{ background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 12, overflow: 'hidden', marginBottom: 28 }}>
          {cancellations.rows.map((r, i) => {
            const w = formatWindow(r.deadlineAtMs - cancellations.loadedAtMs);
            const busy = busyRow === r.id;
            return (
              <div key={r.id} style={{ display: 'flex', alignItems: 'center', gap: 16, padding: '14px 18px', borderTop: i ? '1px solid var(--border)' : 'none', flexWrap: 'wrap', opacity: busy ? 0.5 : 1 }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 10, flex: '1 1 240px', minWidth: 0, cursor: busy ? 'default' : 'pointer' }}>
                  <input type="checkbox" data-write disabled={busy} checked={false}
                    onChange={() => resolveRow(r.id, 'cancelled')}
                    aria-label={`Cancelled ${r.domain} at NameSilo`} />
                  <span style={{ minWidth: 0 }}>
                    <span style={{ display: 'block', color: 'var(--text)', fontSize: 14, fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {r.domain} <span style={{ color: 'var(--text2)', fontWeight: 400 }}>· {r.kind === 'transfer' ? 'transfer' : 'registration'} · {r.source === 'self-serve' ? 'customer self-serve' : 'staff refund'}</span>
                    </span>
                    <span style={{ display: 'block', color: 'var(--text2)', fontSize: 12, marginTop: 2 }}>
                      {r.customerEmail || '—'}{r.namesiloOrderId ? ` · NameSilo order ${r.namesiloOrderId}` : ''}
                    </span>
                  </span>
                </label>
                <div style={{ minWidth: 120, textAlign: 'right', fontSize: 12, fontWeight: 600, color: w.overdue ? 'var(--accent2)' : 'var(--text2)' }}>
                  {w.overdue ? '🔴 ' : '⏳ '}{w.label}
                </div>
                <button data-write disabled={busy} onClick={() => resolveRow(r.id, 'kept')}
                  title="A goodwill refund on a domain the customer is keeping: nothing to cancel."
                  style={{ background: 'var(--bg3)', color: 'var(--text2)', border: '1px solid var(--border)', borderRadius: 7, padding: '6px 12px', fontSize: 12, fontWeight: 600, cursor: busy ? 'default' : 'pointer' }}>
                  Not cancelling
                </button>
              </div>
            );
          })}
        </div>
      )}

      {/* Recovery queue — act before the window closes */}
      <h2 style={{ fontSize: 13, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--text2)', marginBottom: 12 }}>
        Recovery queue — act before the window closes
      </h2>
      {rec.closingSoon.length === 0 ? (
        <div style={{ padding: '20px', background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 12, color: 'var(--text2)', fontSize: 14, marginBottom: 28 }}>
          ✓ Nothing to act on — no recovery windows closing.
        </div>
      ) : (
        <div style={{ background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 12, overflow: 'hidden', marginBottom: 28 }}>
          {rec.closingSoon.map((l, i) => {
            const w = formatWindow(l.msUntilClose);
            return (
              <div key={l.lineId} style={{ display: 'flex', alignItems: 'center', gap: 16, padding: '14px 18px', borderTop: i ? '1px solid var(--border)' : 'none', flexWrap: 'wrap' }}>
                <div style={{ flex: '1 1 200px', minWidth: 0 }}>
                  <div style={{ color: 'var(--text)', fontSize: 14, fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {l.productId || l.lineId} <span style={{ color: 'var(--text2)', fontWeight: 400 }}>· {l.supplier}</span>
                  </div>
                  <div style={{ color: 'var(--text2)', fontSize: 12, marginTop: 2 }}>{l.customerEmail || '—'}</div>
                </div>
                <div style={{ color: 'var(--text)', fontSize: 14, fontWeight: 600, minWidth: 64, textAlign: 'right' }}>{usd(l.outflowCents)}</div>
                <div style={{ minWidth: 120, textAlign: 'right', fontSize: 12, fontWeight: 600, color: w.overdue ? 'var(--accent2)' : 'var(--text2)' }}>
                  {w.overdue ? '🔴 ' : '⏳ '}{w.label}
                </div>
                <LineActions lineId={l.lineId} busyLine={busyLine} onAct={act} />
              </div>
            );
          })}
        </div>
      )}

      {/* Underwater — each line with its disposition */}
      <h2 style={{ fontSize: 13, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--text2)', marginBottom: 12 }}>
        Underwater lines {uw.count > (uw.worst?.length || 0) ? `(top ${uw.worst.length} of ${uw.count})` : ''}
      </h2>
      <UnderwaterLines uw={uw} busyLine={busyLine} onAct={act} />
    </div>
  );
}
